import { ContentSection, DividerList, OptimusProvider } from '@mews-commander/ui';
import { IconName } from '@optimus-web/icons';
import { Banner, IconButton, Stack } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { useDialogForm } from '@mews-commander/form';
import { DefaultPolicySection } from '../../../../../rateGroups/components/sidePanel/PoliciesTabContent/DefaultPolicySection';
import { SettlementConfigurationSection } from '../../../../../rateGroups/components/sidePanel/PoliciesTabContent/SettlementConfigurationSection';
import { WithDefaultPolicy } from '../../../../../rateGroups/types';
import { useSelectedRateGroupContext } from '../../../../contexts/CurrentRateGroupContext';
import { SelectedRateGroup } from '../../../../types';
import { EditDefaultPolicyDialogForm } from '../forms/EditDefaultPoliciesForm';
import { EditConfigurationDialogForm } from '../forms/EditConfigurationsForm';

const EditPaymentButton = (
    params: Pick<SelectedRateGroup, 'options' | 'settlementExtent' | 'settlementMaximumTimeUnits'>
) => {
    const { propertiesWithRateCounts } = useSelectedRateGroupContext();

    const [renderDialog, toggleDialog] = useDialogForm({
        id: 'editConfigurationDialogForm',
        title: lt('CentralRateGroupEditConfigurationFormTitle'),
        renderForm: (props) => (
            <Stack spacing="400" vertical>
                <Banner
                    primaryText={lt('CentralRateGroupEditConfigurationInfoBannerTitle')}
                    secondaryText={lt('CentralRateGroupEditConfigurationInfoBannerDescription', {
                        rateGroupCount: propertiesWithRateCounts
                            ? propertiesWithRateCounts.reduce((count, { rateGroupCount }) => count + rateGroupCount, 0)
                            : 0,
                        propertyCount: propertiesWithRateCounts ? propertiesWithRateCounts.length : 0,
                    })}
                    variant="info"
                />
                <ContentSection title={lt('CentralRateGroupEditConfigurationSectionTitle')} spacing="300">
                    <EditConfigurationDialogForm {...props} {...params} />
                </ContentSection>
            </Stack>
        ),
    });
    return (
        <>
            <IconButton icon={IconName.Edit} onClick={toggleDialog} size="small" type="submit" />
            <OptimusProvider density="spacious">{renderDialog()}</OptimusProvider>
        </>
    );
};

const EditDefaultPolicyButton = ({ defaultPolicy }: WithDefaultPolicy) => {
    const { propertiesWithRateCounts } = useSelectedRateGroupContext();

    const [renderDialog, toggleDialog] = useDialogForm({
        id: 'editDefaultPolicyForm',
        renderForm: (props) => (
            <Stack spacing="400" vertical>
                <Banner
                    variant="info"
                    primaryText={lt('CentralRateGroupEditDefaultPoliciesInfoBannerTitle')}
                    secondaryText={lt('CentralRateGroupEditDefaultPoliciesInfoBannerDescription', {
                        rateGroupCount: propertiesWithRateCounts
                            ? propertiesWithRateCounts.reduce((count, { rateGroupCount }) => count + rateGroupCount, 0)
                            : 0,
                        propertyCount: propertiesWithRateCounts ? propertiesWithRateCounts.length : 0,
                    })}
                />
                <ContentSection
                    title={lt('RateGroupDefaultPolicySectionTitle')}
                    description={lt('RateGroupDefaultPolicySectionDescription')}
                    spacing="300"
                >
                    <EditDefaultPolicyDialogForm {...props} defaultPolicy={defaultPolicy} />
                </ContentSection>
            </Stack>
        ),
        title: lt('RateGroupDefaultPolicyEditFormTitle'),
    });

    return (
        <>
            <IconButton icon={IconName.Edit} size="small" onClick={toggleDialog} type="submit" />
            <OptimusProvider density="spacious">{renderDialog()}</OptimusProvider>
        </>
    );
};

export const PoliciesTabContent = () => {
    const selectedRateGroup = useSelectedRateGroupContext();
    const options = selectedRateGroup.options ?? [];
    const validOptions = options.filter((option) =>
        ['AutomaticDepositSettlement', 'CreateSettlementTask'].includes(option)
    ) as Array<'AutomaticDepositSettlement' | 'CreateSettlementTask'>;

    return (
        <DividerList spacing="400">
            <SettlementConfigurationSection
                {...selectedRateGroup}
                options={validOptions}
                rightContent={<EditPaymentButton {...selectedRateGroup} />}
            />
            <DefaultPolicySection
                defaultPolicy={selectedRateGroup.defaultPolicy}
                options={options}
                rightContent={<EditDefaultPolicyButton defaultPolicy={selectedRateGroup.defaultPolicy} />}
            />
        </DividerList>
    );
};
